import { useEffect, useState } from 'react';
import { ResourceCatalogComponent } from '../../catalog/types';
import { DataTable, DataTableValueArray } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  DevHubIconButton,
  DevHubLink,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import LaunchIcon from '@material-ui/icons/Launch';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { ComponentsFilter } from './ComponentsFilter';

const PER_PAGE_OPTIONS = [25, 50, 100];

/**
 * Props for ComponentsTable
 */
type ComponentsTableProps = {
  components: ResourceCatalogComponent[];
  loading: boolean;
  totalCount: number | undefined;
  onPageChange?: (page: number, rowsPerPage: number) => void;
};

/**
 * Table component for application components
 * @param props The props for the component
 */
export const ComponentsTable = (props: ComponentsTableProps) => {
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PER_PAGE_OPTIONS[0]);

  // Set loading while we have less than the first page size unless we have all of them
  const loading =
    props.components.length <
    Math.min(rowsPerPage, props.totalCount ?? rowsPerPage);

  const values = props.components
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name)) as DataTableValueArray;

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const nameTemplate = (rowData: ResourceCatalogComponent) => {
    const componentId = rowData.id;
    return (
      <DevHubLink href={`/catalog/default/component/${componentId}`}>
        {rowData.name}
      </DevHubLink>
    );
  };

  const sourceTemplate = (rowData: ResourceCatalogComponent) => {
    return (
      <DevHubTooltip title={rowData.sourceLocationUrl}>
        <DevHubIconButton
          size="small"
          onClick={() => window.open(rowData.sourceLocationUrl, '_blank')}
        >
          <LaunchIcon color="primary" fontSize="small" />
        </DevHubIconButton>
      </DevHubTooltip>
    );
  };

  const idTemplate = (rowData: ResourceCatalogComponent) => {
    return (
      <DevHubTooltip title={rowData.id}>
        <DevHubIconButton
          size="small"
          onClick={() => navigator.clipboard.writeText(String(rowData.id))}
        >
          <FileCopyIcon color="primary" fontSize="small" />
        </DevHubIconButton>
      </DevHubTooltip>
    );
  };

  const header = () => {
    return (
      <ComponentsFilter
        onChange={e => setGlobalFilterValue(e.target.value)}
        onClear={() => setGlobalFilterValue('')}
        placeholder="Filter by ID, Name, Description, or Source"
        style={{ fontSize: '90%' }}
        value={globalFilterValue}
      />
    );
  };

  const onPageChange = (event: any) => {
    const newPage = event.page;
    const newRows = event.rows;

    setCurrentPage(newPage);
    setRowsPerPage(newRows);

    // Calculate how many items we need loaded for this page
    const itemsNeeded = (newPage + 1) * newRows;

    // If we don't have enough items loaded, request more
    if (props.components.length < itemsNeeded && props.onPageChange) {
      props.onPageChange(newPage, newRows);
    }
  };

  return (
    <DataTable
      value={values}
      globalFilter={globalFilterValue}
      globalFilterFields={['id', 'name', 'description', 'sourceLocationUrl']}
      header={header}
      loading={loading}
      paginator
      paginatorPosition="bottom"
      rows={rowsPerPage}
      rowsPerPageOptions={PER_PAGE_OPTIONS}
      size="small"
      sortMode="multiple"
      totalRecords={props.totalCount}
      first={currentPage * rowsPerPage}
      onPage={onPageChange}
      style={{ lineHeight: '1' }}
    >
      <Column
        field="id"
        header="ID"
        style={{ width: '7%' }}
        body={idTemplate}
      />
      <Column
        body={nameTemplate}
        field="name"
        header="Name"
        sortable
        style={{ width: '23%' }}
      />
      <Column field="type" header="Type" sortable style={{ width: '10%' }} />
      <Column
        field="lifecycle"
        header="Lifecycle"
        sortable
        style={{ width: '10%' }}
      />
      <Column
        field="description"
        header="Description"
        style={{ width: '43%' }}
      />
      <Column
        field="sourceLocationUrl"
        header="Source"
        headerStyle={{ textAlign: 'center' }}
        body={sourceTemplate}
        bodyStyle={{ textAlign: 'center' }}
        style={{ width: '7%' }}
      />
    </DataTable>
  );
};
